/**
 * Shell — the desktop sidebar + top bar and the mobile app bar + bottom navigation with the
 * "עוד" sheet (spec §3.1–§3.2), plus the global elements of §3.4: the period picker, the
 * freshness chip, the refresh dialog with its credits warning, the notifications bell and the
 * EXPIRED banner.
 */

import type { ReactNode } from 'react';
import type { Alert, PeriodParam, SummaryResponse } from '../../../shared-types.ts';
import { fmtDateTime, initial, monthName, relTime } from '../format.ts';
import { addMonths, monthOf, periodChipLabel } from '../dates.ts';
import { ALERT_ICON, he, providerName } from '../i18n/he.ts';
import { useApp } from '../hooks/useApp.tsx';
import { useIsMobile } from '../hooks/useMediaQuery.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { buildPath, type ScreenName } from '../router.ts';
import { Icon } from './Icon.tsx';
import { Ltr } from './ui.tsx';

const NAV: Array<{ screen: ScreenName; label: string; icon: string }> = [
  { screen: 'home', label: he.nav.home, icon: 'home' },
  { screen: 'transactions', label: he.nav.transactions, icon: 'list' },
  { screen: 'categories', label: he.nav.categories, icon: 'bars' },
  { screen: 'cards', label: he.nav.cards, icon: 'card' },
  { screen: 'loans', label: he.nav.loans, icon: 'bank' },
  { screen: 'networth', label: he.nav.networth, icon: 'trend' },
];

const TABS: Array<{ screen: ScreenName; label: string; icon: string }> = [
  { screen: 'home', label: he.nav.home, icon: 'home' },
  { screen: 'transactions', label: he.nav.transactions, icon: 'list' },
  { screen: 'cards', label: he.nav.cards, icon: 'card' },
  { screen: 'categories', label: he.nav.categories, icon: 'bars' },
];

const PERIOD_SCREENS: ReadonlySet<ScreenName> = new Set<ScreenName>(['home', 'transactions', 'categories']);

export function freshnessView(summary: SummaryResponse | undefined): { text: string; tone: '' | 'warn' | 'critical' } {
  if (!summary) return { text: '', tone: '' };
  const { freshness } = summary;
  if (freshness.level === 'none' || !freshness.lastFetchedAt) return { text: he.freshness.none, tone: 'critical' };
  if (freshness.level === 'stale_critical') return { text: he.freshness.critical(Math.floor((freshness.ageHours ?? 72) / 24)), tone: 'critical' };
  if (freshness.level === 'stale_warn') return { text: he.freshness.warn, tone: 'warn' };
  return { text: he.freshness.fresh(relTime(freshness.lastFetchedAt)), tone: '' };
}

export function alertTarget(alert: Alert): string {
  switch (alert.type) {
    case 'card_due_soon':
      return buildPath('cards', alert.entityId);
    case 'coverage_low':
      return buildPath('cards');
    case 'category_over_baseline':
      return buildPath('categories', alert.entityId);
    case 'large_transaction':
      return buildPath('transactions', null, { tx: alert.entityId ?? '' });
    case 'returned_payment':
      return buildPath('transactions', null, { kind: 'returns' });
    case 'connection_expired':
      return buildPath('settings', 'connections');
    case 'new_account':
      return buildPath('settings', 'accounts');
    default:
      return buildPath('settings', 'connections');
  }
}

export function Sidebar({ summary }: { summary: SummaryResponse | undefined }) {
  const { route } = useRouter();
  const { user, openMenu } = useApp();
  const unread = summary?.alerts.unreadCount ?? 0;
  return (
    <aside className="sidebar" aria-label={he.nav.mainNav}>
      <div className="brand">
        <div className="name">{he.app.name}</div>
        <div className="hh">{he.app.household}</div>
      </div>
      <nav className="nav" aria-label={he.nav.mainNav}>
        {NAV.map((item) => (
          <Link key={item.screen} to={buildPath(item.screen)} aria-current={route.screen === item.screen ? 'page' : undefined}>
            <Icon name={item.icon} />
            <span>{item.label}</span>
          </Link>
        ))}
        <hr />
        <Link to={buildPath('alerts')} aria-current={route.screen === 'alerts' ? 'page' : undefined}>
          <Icon name="bell" />
          <span>{he.nav.alerts}</span>
          {unread ? (
            <span className="cnt" aria-hidden="true">
              {unread}
            </span>
          ) : null}
        </Link>
        <Link to={buildPath('settings')} aria-current={route.screen === 'settings' ? 'page' : undefined}>
          <Icon name="settings" />
          <span>{he.nav.settings}</span>
        </Link>
      </nav>
      <button
        type="button"
        className="user"
        aria-haspopup="menu"
        onClick={(event) => openMenu(event.currentTarget, <UserMenu />)}
      >
        <span className="avatar" aria-hidden="true">
          {initial(user?.displayName ?? '')}
        </span>
        <span className="grow">
          <span style={{ display: 'block', fontSize: '14px', fontWeight: 500 }}>{user?.displayName}</span>
          <span className="meta">{user?.role === 'owner' ? he.roles.owner : he.roles.member}</span>
        </span>
        <Icon name="chevU" className="t3" />
      </button>
    </aside>
  );
}

function UserMenu() {
  const { user, api, setUser, closeLayer } = useApp();
  const { navigate } = useRouter();
  return (
    <>
      <div className="mh">
        <b style={{ color: 'var(--text-1)' }}>{user?.displayName}</b>
        <br />
        <Ltr>{user?.email}</Ltr>
      </div>
      <hr />
      <Link className="mi" role="menuitem" to={buildPath('settings')} onClick={closeLayer}>
        <Icon name="settings" className="s" />
        {he.nav.settings}
      </Link>
      <hr />
      <button
        type="button"
        className="mi"
        role="menuitem"
        onClick={() => {
          closeLayer();
          void api.logout().catch(() => undefined);
          setUser(null);
          navigate(buildPath('login'));
        }}
      >
        <Icon name="logout" className="s" />
        {he.nav.signOut}
      </button>
    </>
  );
}

function PeriodMenu({ summary }: { summary: SummaryResponse | undefined }) {
  const { period, setPeriod, closeLayer } = useApp();
  const asOfMonth = summary ? monthOf(summary.asOf) : '2026-09';
  const options: Array<[PeriodParam, string]> = [
    ['month', he.period.month(monthName(asOfMonth, false))],
    ['prev', he.period.prev(monthName(addMonths(asOfMonth, -1), false))],
    ['3m', he.period.m3],
    ['12m', he.period.m12],
  ];
  return (
    <>
      {options.map(([key, label]) => (
        <button
          key={key}
          type="button"
          className="mi"
          role="menuitemradio"
          aria-checked={period === key}
          onClick={() => {
            setPeriod(key);
            closeLayer();
          }}
        >
          {label}
          <Icon name="check" className="chk" />
        </button>
      ))}
      <hr />
      <div className="mh">{he.period.custom}</div>
      <CustomRange asOf={summary?.asOf ?? '2026-09-06'} historyFrom={summary?.period.historyFrom ?? '2025-09-01'} />
    </>
  );
}

function CustomRange({ asOf, historyFrom }: { asOf: string; historyFrom: string }) {
  const { setPeriod, closeLayer, toast } = useApp();
  return (
    <form
      className="row"
      style={{ padding: '4px 8px 8px', gap: '8px' }}
      onSubmit={(event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const from = (form.elements.namedItem('from') as HTMLInputElement).value;
        const to = (form.elements.namedItem('to') as HTMLInputElement).value;
        if (!from || !to) return;
        const [a, b] = from <= to ? [from, to] : [to, from];
        if ((Date.parse(b) - Date.parse(a)) / 86400000 > 366) {
          toast(he.period.tooLong, { error: true });
          return;
        }
        setPeriod(`${a},${b > asOf ? asOf : b}` as PeriodParam);
        closeLayer();
      }}
    >
      <input className="input" type="date" name="from" aria-label={he.period.from} min={historyFrom} max={asOf} defaultValue={historyFrom} />
      <input className="input" type="date" name="to" aria-label={he.period.to} min={historyFrom} max={asOf} defaultValue={asOf} />
      <button type="submit" className="btn primary sm">
        {he.app.apply}
      </button>
    </form>
  );
}

export function PeriodChip({ summary, inline }: { summary: SummaryResponse | undefined; inline?: boolean }) {
  const { openMenu, openSheet, period } = useApp();
  const isMobile = useIsMobile();
  const label = summary ? periodChipLabel(summary.period) : (period as string);
  const button = (
    <button
      type="button"
      className="pill"
      aria-haspopup="menu"
      onClick={(event) => {
        if (isMobile) openSheet({ title: he.period.label, content: <div className="menu" style={{ position: 'static', boxShadow: 'none', border: 0, padding: 0 }}><PeriodMenu summary={summary} /></div> });
        else openMenu(event.currentTarget, <PeriodMenu summary={summary} />, { wide: true });
      }}
    >
      <Icon name="calendar" className="s" />
      <span>{label}</span>
      <Icon name="chevD" className="xs" />
    </button>
  );
  if (inline) return <div className="only-m" style={{ padding: '4px 0 0' }}>{button}</div>;
  return <div className="period-wrap">{button}</div>;
}

function BellMenu({ summary }: { summary: SummaryResponse | undefined }) {
  const { closeLayer, api } = useApp();
  const alerts = summary?.alerts.unread ?? [];
  return (
    <>
      <div className="mh" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{he.nav.alerts}</span>
        {alerts.length ? (
          <button
            type="button"
            className="link"
            style={{ fontSize: '12px' }}
            onClick={() => {
              closeLayer();
              void api.readAllAlerts();
            }}
          >
            {he.alerts.readAll}
          </button>
        ) : null}
      </div>
      {alerts.length ? (
        alerts.slice(0, 5).map((alert) => (
          <Link key={alert.id} className="mi" role="menuitem" to={alertTarget(alert)} style={{ alignItems: 'flex-start' }} onClick={() => { closeLayer(); void api.updateAlert(alert.id, { read: true }); }}>
            <span className={`aic ${alert.severity}`} style={{ width: 28, height: 28, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', background: `var(--${alert.severity}-soft)`, color: `var(--${alert.severity}-ink)` }}>
              <Icon name={ALERT_ICON[alert.type]} className="xs" />
            </span>
            <span className="grow" style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '13px', fontWeight: alert.readAt ? 500 : 600, whiteSpace: 'normal' }}>{alert.title}</span>
              <span className="meta">{relTime(alert.createdAt)}</span>
            </span>
          </Link>
        ))
      ) : (
        <div className="empty" style={{ padding: '16px' }}>
          <Icon name="inbox" />
          <p>{he.alerts.empty}</p>
        </div>
      )}
      <hr />
      <Link className="mi" role="menuitem" to={buildPath('alerts')} onClick={closeLayer}>
        <Icon name="bell" className="s" />
        {he.alerts.allAlerts}
      </Link>
    </>
  );
}

export function useRefreshDialog(summary: SummaryResponse | undefined) {
  const { openDialog, startSync, user, toast } = useApp();
  const { navigate } = useRouter();
  return () => {
    if (user?.role === 'member') {
      toast(he.top.refreshMember, { error: true });
      return;
    }
    const active = (summary?.connections ?? []).filter((connection) => connection.status === 'ACTIVE');
    if (!active.length) {
      openDialog({
        title: he.refresh.title,
        text: he.refresh.noActive,
        actions: [
          { label: he.refresh.toConnections, tone: 'primary', onClick: () => navigate(buildPath('settings', 'connections')) },
          { label: he.app.cancel },
        ],
      });
      return;
    }
    const credits = summary?.credits;
    const text =
      credits && credits.plan && credits.usedThisMonth !== null
        ? he.refresh.body(String(credits.plan.credits - credits.usedThisMonth), String(credits.plan.credits))
        : he.refresh.bodyUnknown;
    openDialog({
      title: he.refresh.title,
      text,
      actions: [
        { label: he.refresh.confirm, tone: 'primary', onClick: () => startSync(true) },
        { label: he.app.cancel },
      ],
    });
  };
}

export function Topbar({ summary, title, back }: { summary: SummaryResponse | undefined; title: string; back?: string | null }) {
  const { route } = useRouter();
  const { user, openMenu } = useApp();
  const fresh = freshnessView(summary);
  const refresh = useRefreshDialog(summary);
  const isMember = user?.role === 'member';
  const unread = summary?.alerts.unreadCount ?? 0;
  return (
    <header className="topbar">
      {back ? (
        <Link className="btn-icon mback" to={back} aria-label={he.app.back}>
          <Icon name="chevR" />
        </Link>
      ) : null}
      <h1>{title}</h1>
      <span className="sp" />
      {PERIOD_SCREENS.has(route.screen) ? <PeriodChip summary={summary} /> : null}
      {fresh.text ? (
        <span className={`fresh ${fresh.tone}`} role="status">
          <Icon name={fresh.tone ? 'alert' : 'clock'} className="xs" />
          {fresh.text}
        </span>
      ) : null}
      <button type="button" className="btn ghost refresh-btn" aria-disabled={isMember || undefined} title={isMember ? he.top.refreshMember : undefined} onClick={refresh}>
        <Icon name="refresh" className="s" />
        {he.top.refresh}
      </button>
      <BellButton summary={summary} />
      <button type="button" className="btn-icon only-m" aria-label={he.top.refresh} aria-disabled={isMember || undefined} onClick={refresh}>
        <Icon name="refresh" />
      </button>
      <button
        type="button"
        className="btn-icon user-btn"
        aria-label={he.top.userMenu}
        aria-haspopup="menu"
        onClick={(event) => openMenu(event.currentTarget, <UserMenu />)}
      >
        <span className="avatar" aria-hidden="true">
          {initial(user?.displayName ?? '')}
        </span>
      </button>
      <span className="sr-only" role="status" aria-atomic="true">
        {unread ? he.alerts.unreadCount(unread) : he.alerts.noneNew}
      </span>
    </header>
  );
}

function BellButton({ summary }: { summary: SummaryResponse | undefined }) {
  const { openMenu } = useApp();
  const { navigate } = useRouter();
  const isMobile = useIsMobile();
  const unread = summary?.alerts.unreadCount ?? 0;
  return (
    <button
      type="button"
      className="btn-icon bell"
      aria-haspopup="true"
      aria-label={he.top.notifications}
      onClick={(event) => {
        if (isMobile) navigate(buildPath('alerts'));
        else openMenu(event.currentTarget, <BellMenu summary={summary} />, { wide: true });
      }}
    >
      <Icon name="bell" />
      {unread ? (
        <span className="badge" aria-hidden="true">
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </button>
  );
}

export function FreshRow({ summary }: { summary: SummaryResponse | undefined }) {
  const fresh = freshnessView(summary);
  const refresh = useRefreshDialog(summary);
  if (!fresh.text) return <div className="freshrow" />;
  return (
    <div className="freshrow">
      <button type="button" className={fresh.tone} onClick={refresh}>
        <Icon name={fresh.tone ? 'alert' : 'clock'} className="xs" />
        {fresh.text} · {he.top.clickToRefresh}
      </button>
    </div>
  );
}

export function BottomNav({ summary }: { summary: SummaryResponse | undefined }) {
  const { route } = useRouter();
  const { openSheet, closeLayer, api, setUser } = useApp();
  const { navigate } = useRouter();
  const unread = summary?.alerts.unreadCount ?? 0;
  const moreActive =
    route.screen === 'loans' || route.screen === 'networth' || route.screen === 'alerts' || route.screen === 'settings';
  return (
    <nav className="bottomnav" aria-label={he.nav.bottomNav}>
      {TABS.map((tab) => (
        <Link key={tab.screen} to={buildPath(tab.screen)} aria-current={route.screen === tab.screen ? 'page' : undefined}>
          <Icon name={tab.icon} />
          <span>{tab.label}</span>
        </Link>
      ))}
      <a
        href={buildPath('networth')}
        aria-current={moreActive ? 'page' : undefined}
        onClick={(event) => {
          event.preventDefault();
          openSheet({
            title: he.nav.more,
            content: (
              <>
                <Link className="mi" to={buildPath('loans')} onClick={closeLayer}>
                  <Icon name="bank" />
                  {he.nav.loans}
                </Link>
                <Link className="mi" to={buildPath('networth')} onClick={closeLayer}>
                  <Icon name="trend" />
                  {he.nav.networth}
                </Link>
                <Link className="mi" to={buildPath('alerts')} onClick={closeLayer}>
                  <Icon name="bell" />
                  {he.nav.alertsCenter}
                  {unread ? <span className="cnt">{unread}</span> : null}
                </Link>
                <Link className="mi" to={buildPath('settings')} onClick={closeLayer}>
                  <Icon name="settings" />
                  {he.nav.settings}
                </Link>
                <hr className="divider" />
                <button
                  type="button"
                  className="mi"
                  onClick={() => {
                    closeLayer();
                    void api.logout().catch(() => undefined);
                    setUser(null);
                    navigate(buildPath('login'));
                  }}
                >
                  <Icon name="logout" />
                  {he.nav.logout}
                </button>
              </>
            ),
          });
        }}
      >
        <Icon name="more" />
        <span>{he.nav.more}</span>
        {unread ? (
          <span className="cnt" aria-hidden="true">
            {unread}
          </span>
        ) : null}
      </a>
    </nav>
  );
}

/**
 * REDESIGN §8: "The EXPIRED banner stops shouting. It becomes a single amber line under the top
 * bar with a `חבר מחדש` link, not a red panel above the content. When the data is still recent,
 * it lives in `אפשר לחכות` instead."
 *
 * So this renders nothing while `freshness.level === 'fresh'` — the lapsed consent is then one
 * row of the home screen's `אפשר לחכות` bucket (`connection_expired` is pinned `later` there) —
 * and one amber line the moment the stale data actually costs the reader something.
 */
export function ExpiredBanner({ summary }: { summary: SummaryResponse | undefined }) {
  const { bannerHiddenFor, hideBannerToday, toast } = useApp();
  if (!summary) return null;
  const expired = summary.connections.filter((connection) => connection.needsRelink);
  if (!expired.length || bannerHiddenFor === summary.asOf) return null;
  if (summary.freshness.level === 'fresh') return null;
  const names = expired.map((connection) => providerName(connection.providerId)).join(', ');
  return (
    <div className="fk-line-notice" role="status">
      <Icon name="alert" className="xs" />
      <span className="grow">{he.now.expiredLine(names, fmtDateTime(summary.freshness.lastFetchedAt))}</span>
      <Link className="link" to={buildPath('settings', 'connections')}>
        {he.now.reconnect}
        <Icon name="chevL" className="xs" />
      </Link>
      <button
        type="button"
        className="btn-icon"
        aria-label={he.banner.hideToday}
        onClick={() => {
          hideBannerToday(summary.asOf);
          toast(he.banner.hidden);
        }}
      >
        <Icon name="x" className="xs" />
      </button>
    </div>
  );
}

/** Wraps a screen's content with the busy state of a running refresh (50%, aria-busy). */
export function ScreenBody({ busy, children }: { busy: boolean; children: ReactNode }) {
  return (
    <main className={`appmain${busy ? ' busy' : ''}`} id="main-content" tabIndex={-1} aria-busy={busy || undefined}>
      {children}
    </main>
  );
}
